import template from 'lodash-es/template';
import uniqWith from 'lodash-es/uniqWith';
import Fuse from 'fuse.js';
import templateSearchDropdown from './template-search-dropdown.html';
import 'css/abtests/CTS-627/modules/AutofillDropdown.less';

const KEY_ARROW_DOWN = 40;
const KEY_ARROW_UP = 38;
const KEY_ENTER = 13;

export default class AutofillClickableDropdown {
  constructor(options) {
    // Initialize from input
    this.containerEl = options.containerEl;
    this.inputEl = options.inputEl;
    this.dropdownEl = options.dropdownEl;
    this.autofillList = options.autofillList;
    this.autofillSearchOptions = options.autofillSearchOptions || {};
    this.autofillDropdownOptions = options.autofillDropdownOptions || {};
    this.onListItemSelectCallback =
      options.onListItemSelectCallback || (() => {});

    // Ensure that all autofill ids are unique
    this.autofillList = this.autofillList.map((autofill, index) => {
      autofill.id = index;
      return autofill;
    });

    // Initialize internal things
    this._listItemEls = [];
    this._highlightedListItemIndex = -1;
    this.fuzzySearcher = new Fuse(
      this.autofillList,
      this.autofillSearchOptions,
    );
    this._onListItemClicked = this._onListItemClicked.bind(this);
    this._onListItemFocused = this._onListItemFocused.bind(this);
    this._bindEvents();
  }

  setAutofillList(newAutofillList) {
    // Replace old list
    this.autofillList = newAutofillList;
    // Ensure that all autofill ids are unique
    this.autofillList = this.autofillList.map((autofill, index) => {
      autofill.id = index;
      return autofill;
    });
    // Set up Fuzzy Searcher again
    this.fuzzySearcher = new Fuse(
      this.autofillList,
      this.autofillSearchOptions,
    );
  }

  getAutofillList() {
    return this.autofillList;
  }

  _selectItem(itemId, event) {
    const clickedEntry = this.autofillList.filter(
      item => parseInt(item.id, 10) === parseInt(itemId, 10),
    )[0];
    this.inputEl.value = clickedEntry.name;
    this.onListItemSelectCallback(clickedEntry, itemId, event);
  }

  _bindEvents() {
    this.inputEl.addEventListener('keydown', event => {
      // For keyboard navigations mostly
      // Keyboard navigation only if the dropdown is active
      if (!this.isDropdownActive()) {
        return;
      }

      // Handle down key. Cycle to the list item previous to the selected one.
      if (event.keyCode === KEY_ARROW_DOWN) {
        event.preventDefault();
        this.highlightListItem(this.getHighlightedListItemIndex() + 1);
        return;
      }

      // Handle up key. Cycle to the list item next to the selected one.
      if (event.keyCode === KEY_ARROW_UP) {
        event.preventDefault();
        this.highlightListItem(this.getHighlightedListItemIndex() - 1);
        return;
      }

      // Handle enter key. Select the currently selected list item.
      if (event.keyCode === KEY_ENTER) {
        // If there is a highlighted list item, select
        if (this.hasHighlightedItem()) {
          event.preventDefault();
          const highlightedListItemEl = this._listItemEls[
            this.getHighlightedListItemIndex()
          ];
          this._selectItem(
            highlightedListItemEl.getAttribute('data-id'),
            event,
          );
        }

        // Always hide dropdown on any enter press
        this.hideDropdown();
      }
    });

    this.inputEl.addEventListener('keyup', event => {
      // Dont respond to the keyups of navigation keys
      if (
        event.keyCode === KEY_ARROW_DOWN ||
        event.keyCode === KEY_ARROW_UP ||
        event.keyCode === KEY_ENTER
      ) {
        return;
      }

      const searchQuery = event.target.value;
      let listToRender = this.fuzzySearcher.search(searchQuery);

      // Dedupe if set in options
      if (this.autofillDropdownOptions.uniqueWith) {
        listToRender = uniqWith(
          listToRender,
          this.autofillDropdownOptions.uniqueWith,
        );
      }

      // Trim list if set in options
      if (this.autofillDropdownOptions.showTop) {
        listToRender = listToRender.slice(
          0,
          this.autofillDropdownOptions.showTop,
        );
      }

      // Option to add priority to the autofill dropdown
      if (this.autofillDropdownOptions.prioritiseByField) {
        const field = this.autofillDropdownOptions.prioritiseByField;
        listToRender.sort((a, b) => {
          const priorityOfA = a[field] || 0;
          const priorityOfB = b[field] || 0;
          if (priorityOfA < priorityOfB) {
            return 1;
          } else if (priorityOfA > priorityOfB) {
            return -1;
          }
          return 0;
        });
      }

      this.renderList(listToRender);
    });
  }

  /**
   * NOTE: also resets the highlighted item to none (-1 index)
   */
  hideDropdown() {
    this.highlightListItem(-1);
    this.dropdownEl.classList.add('is-hidden');
  }

  showDropdown() {
    this.dropdownEl.classList.remove('is-hidden');
  }

  isDropdownActive() {
    return !this.dropdownEl.classList.contains('is-hidden');
  }

  hasHighlightedItem() {
    return (
      this.getHighlightedListItemIndex() >= 0 &&
      this.getHighlightedListItemIndex() < this._listItemEls.length
    );
  }

  getHighlightedListItemIndex() {
    return this._highlightedListItemIndex;
  }

  highlightListItem(index) {
    // If the last highlighted list item is not -1 or exists then
    // de-highlight it.
    if (
      this._highlightedListItemIndex !== -1 &&
      this._listItemEls[this._highlightedListItemIndex]
    ) {
      this._listItemEls[this._highlightedListItemIndex].classList.remove(
        'is-highlighted',
      );
    }

    // Limit the new index to the last list item and the first list item.
    this._highlightedListItemIndex = Math.max(
      Math.min(index, this._listItemEls.length - 1),
      -1,
    );

    if (this._highlightedListItemIndex !== -1) {
      this._listItemEls[this._highlightedListItemIndex].classList.add(
        'is-highlighted',
      );
    }
  }

  _onListItemFocused(event) {
    this.highlightListItem(event.target.getAttribute('data-index'));
  }

  _onListItemClicked(event) {
    this._selectItem(event.target.getAttribute('data-id'), event);
    this.hideDropdown();
  }

  renderList(dropdownList) {
    // Undelegate events
    Array.from(this.dropdownEl.children).forEach(child => {
      child.removeEventListener('focus', this._onListItemFocused);
      child.removeEventListener('click', this._onListItemClicked);
    });

    // Hide dropdown and do nothing if matches nothing
    if (!dropdownList.length) {
      this.hideDropdown();
      return;
    }

    this.dropdownEl.innerHTML = template(templateSearchDropdown)({
      list: dropdownList,
    });

    // Redelegate events to new children
    Array.from(this.dropdownEl.children).forEach(child => {
      child.addEventListener('focus', this._onListItemFocused);
      child.addEventListener('click', this._onListItemClicked);
    });

    // Save the array of the dropdown list items.
    this._listItemEls = Array.from(this.dropdownEl.children);

    this.showDropdown();
  }
}

AutofillClickableDropdown.fetchAutofillList = () =>
  fetch('/ajax/project/getProjectTitleAutoFills.php').then(res => res.json());
